import type { ReactNode } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { formatNumar } from "@/utils/targetareFormatting";

export type TonTile = "implicit" | "bun" | "atentie" | "rau" | "accent";

export type TileHero = {
  eticheta: string;
  valoare: number | string | null | undefined;
  detaliu?: string;
  ton?: TonTile;
  onClick?: () => void;
  titlu?: string;
};

type HeroPaginaProps = {
  titlu: string;
  descriere?: ReactNode;
  tiles: TileHero[];
  actiuni?: ReactNode;
  isLoading?: boolean;
  copii?: ReactNode;
  coloane?: number;
};

const CLASE_TON: Record<TonTile, string> = {
  implicit: "",
  bun: "text-ds-green-700",
  atentie: "text-amber-700",
  rau: "text-destructive",
  accent: "text-ds-blue-700",
};

function Tile({ tile }: { tile: TileHero }) {
  const valoare =
    typeof tile.valoare === "number"
      ? formatNumar(tile.valoare)
      : (tile.valoare ?? "–");
  const continut = (
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">
        {tile.eticheta}
      </p>
      <p
        className={cn(
          "text-2xl font-heading font-semibold tabular-nums",
          CLASE_TON[tile.ton ?? "implicit"]
        )}
      >
        {valoare}
      </p>
      {tile.detaliu && (
        <p className="mt-1 text-xs text-muted-foreground">{tile.detaliu}</p>
      )}
    </CardContent>
  );
  if (tile.onClick) {
    return (
      <Card
        role="button"
        tabIndex={0}
        title={tile.titlu ?? "Deschide lista firmelor"}
        className="cursor-pointer transition-colors hover:border-ds-blue-300 hover:bg-ds-blue-50/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={tile.onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            tile.onClick?.();
          }
        }}
      >
        {continut}
      </Card>
    );
  }
  return <Card>{continut}</Card>;
}

export function HeroPagina({
  titlu,
  descriere,
  tiles,
  actiuni,
  isLoading,
  copii,
  coloane,
}: HeroPaginaProps) {
  const nr = coloane ?? Math.min(Math.max(tiles.length, 2), 6);
  const clasaGrid =
    nr >= 6
      ? "md:grid-cols-6"
      : nr === 5
        ? "md:grid-cols-5"
        : nr === 4
          ? "md:grid-cols-4"
          : nr === 3
            ? "md:grid-cols-3"
            : "md:grid-cols-2";
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-xl font-heading font-semibold">{titlu}</h2>
          {descriere && (
            <div className="max-w-3xl text-sm text-muted-foreground">
              {descriere}
            </div>
          )}
        </div>
        {actiuni && (
          <div className="flex flex-wrap items-center gap-2">{actiuni}</div>
        )}
      </div>
      {isLoading ? (
        <div className={cn("grid gap-3 grid-cols-2", clasaGrid)}>
          {Array.from({ length: nr }).map((_, i) => (
            <Skeleton key={i} className="h-20" />
          ))}
        </div>
      ) : (
        tiles.length > 0 && (
          <div className={cn("grid gap-3 grid-cols-2", clasaGrid)}>
            {tiles.map((t) => (
              <Tile key={t.eticheta} tile={t} />
            ))}
          </div>
        )
      )}
      {copii}
    </div>
  );
}
